import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AuthenticatedUser, LoginInput } from '@taskflow/shared';
import { ApiError, setSessionExpiredHandler } from '../../services/httpClient';
import { authApi } from './api';

/**
 * Session state.
 *
 * `/auth/me` is the single source of truth, held in the TanStack Query cache so it
 * is shared by every consumer and invalidated in one place. The context exists to
 * give that query a name and to expose the sign-in and sign-out actions — it holds
 * no duplicate copy of the user, because two sources of truth for "who am I" is how
 * a UI ends up showing the wrong person's name.
 *
 * The `retry: false` matters: `/auth/me` returning 401 is the *expected* answer for
 * a signed-out visitor, not a failure worth retrying. The httpClient already
 * attempts one silent token refresh before that 401 reaches here.
 */

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  status: AuthStatus;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-reads the session, e.g. after a role change. */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (error) {
        // Anonymous is a valid state, not an error. Returning null keeps the query
        // in a success state so the UI does not flash an error screen at every
        // signed-out visitor.
        if (error instanceof ApiError && error.isAuthError) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: (result) => {
      // Seed the cache from the login response rather than refetching: the server
      // already returned the user, and a second round trip would only add latency.
      queryClient.setQueryData(AUTH_QUERY_KEY, result.user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      // Settled, not success: if logout fails server-side the local session is
      // already unusable, and leaving a stale user on screen is worse than clearing
      // it. `clear()` drops every cached board and card too — none of it belongs to
      // whoever signs in next.
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
      queryClient.clear();
    },
  });

  // The httpClient calls this when a refresh attempt has already failed, so the
  // session is definitively gone. Clearing the cached user flips the route guards,
  // which is what performs the redirect — one place, no scattered navigate() calls.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      queryClient.setQueryData(AUTH_QUERY_KEY, null);
    });
    return () => setSessionExpiredHandler(null);
  }, [queryClient]);

  const login = useCallback(
    async (input: LoginInput) => {
      await loginMutation.mutateAsync(input);
    },
    [loginMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
  }, [queryClient]);

  const status: AuthStatus = query.isPending
    ? 'loading'
    : query.data
      ? 'authenticated'
      : 'anonymous';

  const value = useMemo<AuthContextValue>(
    () => ({ user: query.data ?? null, status, login, logout, refresh }),
    [query.data, status, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}

/**
 * The signed-in user, asserted.
 *
 * For components that only render behind `<RequireAuth>`, so they are not forced to
 * handle a null user that cannot occur there.
 */
export function useCurrentUser(): AuthenticatedUser {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser used outside an authenticated route');
  return user;
}
